import classNames from "classnames"
import { ClipboardPlus, CloseCircle, Verified } from "components/LEGACY/styled-components/Icons"
import CopyableString from "components/utils/CopyableString"
import ImageUploader from "components/utils/ImageUploader"
import Input from "components/utils/InputField"
import Select from "components/utils/Select"
import { deleteField, getDoc, updateDoc } from "firebase/firestore"
import { useDelayedShop } from "hooks/firestore/advanced/useDelayedShop"
import { useUser } from "hooks/firestore/simple/useUser"
import { useHost } from "hooks/misc/useHost"
import React, { useEffect, useState } from "react"
import sanitizeUrl from "util-functions/sanitizeUrl"
import validator from "validator"
import HeaderBar from "./HeaderBar"
//TODO: Decide what to do about this
import Toggle from "components/utils/Toggle"
import { getShopRef, getShortUrlRef } from "hooks/firestore/getRefs"
import { useSettings } from "hooks/firestore/simple/useSettings"
import { useDisplayToast } from "hooks/misc/useDisplayToast"
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./Settings.module.scss"

export default function SettingsGeneral() {
  const { shop: delayedShop, updateShop, commit } = useDelayedShop()
  const settings = useSettings()
  const displayToast = useDisplayToast()

  const [value, setValue] = useState<Record<string, string>>({
    "name": delayedShop?.name || "",
    "type": delayedShop?.type || "",
    "shorthand": delayedShop?.shorthand || "",
    "linkedInfos.terms": delayedShop?.linkedInfos?.terms || "",
    "linkedInfos.dataPrivacy": delayedShop?.linkedInfos?.dataPrivacy || "",
    "contact.companyName": delayedShop?.contact.companyName || "",
    "contact.representativeFirstName": delayedShop?.contact.representativeFirstName || "",
    "contact.representativeLastName": delayedShop?.contact.representativeLastName || "",
    "contact.address": delayedShop?.contact.address || "",
    "contact.zipCode": delayedShop?.contact.zipCode || "",
    "contact.city": delayedShop?.contact.city || "",
    "contact.website": delayedShop?.contact.website || "",
    "contact.email": delayedShop?.contact.email || "",
    "contact.phone": delayedShop?.contact.phone || "",
    "contact.vatNum": delayedShop?.contact.vatNum || "",
    "contact.registerTown": delayedShop?.contact.registerTown || "",
    "contact.registerNum": delayedShop?.contact.registerNum || "",
  })

  useEffect(() => {
    if (delayedShop) {
      setValue({
        "name": delayedShop.name,
        "type": delayedShop.type || "",
        "shorthand": delayedShop.shorthand || "",
        "linkedInfos.terms": delayedShop.linkedInfos?.terms || "",
        "linkedInfos.dataPrivacy": delayedShop.linkedInfos?.dataPrivacy || "",
        "contact.companyName": delayedShop.contact.companyName,
        "contact.representativeFirstName": delayedShop.contact.representativeFirstName,
        "contact.representativeLastName": delayedShop.contact.representativeLastName,
        "contact.address": delayedShop.contact.address,
        "contact.zipCode": delayedShop.contact.zipCode,
        "contact.city": delayedShop.contact.city,
        "contact.website": delayedShop.contact.website || "",
        "contact.email": delayedShop.contact.email || "",
        "contact.phone": delayedShop.contact.phone || "",
        "contact.vatNum": delayedShop.contact.vatNum || "",
        "contact.registerTown": delayedShop.contact.registerTown || "",
        "contact.registerNum": delayedShop.contact.registerNum || "",
      })
    }
  }, [!!delayedShop])

  const [saveLoading, setSaveLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const checkErrors = () => {
    const addressRegex = /[a-zA-ZäöüÄÖÜ .]+ [0-9]+[a-zA-Z]?/
    const zipCodeRegex = delayedShop?.contact?.country === "AT" ? /^[0-9]{4}$/ : /^[0-9]{5}$/
    const cityRegex = /[a-zA-ZäöüÄÖÜß]+/
    const phoneRegex = /\d+/

    const errors: string[] = []
    const addError = (error: string) => errors.push(error)

    if (!value.name) addError("name")
    if (!value.type) addError("type")
    if (!value.shorthand) addError("shorthand")
    if (!value["contact.companyName"]) addError("contact.companyName")
    if (!addressRegex.test(value["contact.address"])) addError("contact.address")
    if (!value["contact.representativeFirstName"]) addError("contact.representativeFirstName")
    if (!value["contact.representativeLastName"]) addError("contact.representativeLastName")
    if (!zipCodeRegex.test(value["contact.zipCode"])) addError("contact.zipCode")
    if (!cityRegex.test(value["contact.city"])) addError("contact.city")
    if (!phoneRegex.test(value["contact.phone"])) addError("contact.phone")
    if (!validator.isEmail(value["contact.email"])) addError("contact.email")
    if (value["contact.website"] && !validator.isURL(value["contact.website"])) addError("contact.website")
    if (value["linkedInfos.terms"] && !validator.isURL(value["linkedInfos.terms"])) addError("linkedInfos.terms")
    if (value["linkedInfos.dataPrivacy"] && !validator.isURL(value["linkedInfos.dataPrivacy"]))
      addError("linkedInfos.dataPrivacy")
    if (settings?.b2b?.active && value["contact.vatNum"] !== delayedShop?.contact.vatNum) {
      displayToast(
        "Die USt. Identifikationsnummer kann nicht geändert werden, wenn das B2B Feature aktiviert ist",
        "error"
      )
      addError("contact.vatNum")
    }

    setErrors(errors)
    if (errors.length > 0) return false
    return true
  }

  const handleSaveSettings = async () => {
    setSaveLoading(true)
    if (!delayedShop) {
      displayToast("Der Laden hat noch nicht geladen.", "error")
      setSaveLoading(false)
      return
    }

    // Checking for Errors
    const isValid = checkErrors()
    if (!isValid) {
      displayToast("Bitte überprüfe Deine Angaben.", "error")
      setSaveLoading(false)
      return
    }

    const sanitizedShorthand = sanitizeUrl(value.shorthand)
    if (sanitizedShorthand !== value.shorthand) {
      setValue(value => {
        return { ...value, shorthand: sanitizedShorthand }
      })
      displayToast("Die Kurz-URL darf nur alphanumerische Zeichen enthalten", "error")
      setSaveLoading(false)
      return
    }

    if (delayedShop && delayedShop?.shorthand !== sanitizedShorthand) {
      if (!delayedShop.offering?.offeringId) {
        displayToast("Du musst ein Sortiment angelegt haben um die kurz URL zu nutzen", "warning")
      }
      const shorturlDocRef = getShortUrlRef(sanitizedShorthand)
      const shorturlDoc = await getDoc(shorturlDocRef)
      const doc = shorturlDoc.data()
      const currentShorturlOwner = doc?.id
      if (currentShorturlOwner && currentShorturlOwner !== delayedShop._ref.id) {
        displayToast("Diese Kurz-URL ist bereits vergeben. Bitte wähle eine Neue.", "error")
        setErrors([...errors, "value.shorthand"])
        setSaveLoading(false)
        return
      }
    }

    try {
      await commit()
      await updateDoc(delayedShop._ref, {
        ...value,
      })
    } catch (error) {
      displayToast("Etwas ist schief gelaufen", "error")
      setSaveLoading(false)
      return
    }
    displayToast("Erfolgreich gespeichert", "success")
    setSaveLoading(false)
    return
  }

  const labels: Record<string, string> = {
    "name": "Anzeigename*",
    "shorthand": "Kurz-URL*",
    "linkedInfos.terms": "Link zu den AGB",
    "linkedInfos.dataPrivacy": "Link zur Datenschutzerklärung",
    "contact.companyName": "Firmenname",
    "contact.representativeFirstName": "Vorname*",
    "contact.representativeLastName": "Nachname*",
    "contact.address": "Straße + Nummer*",
    "contact.zipCode": "PLZ*",
    "contact.city": "Stadt*",
    "contact.website": "Webseite",
    "contact.phone": "Telefonnummer*",
    "contact.email": "E-Mail*",
    "contact.vatNum": "USt. Identifikationsnummer (VAT)",
    "contact.registerTown": "Ort",
    "contact.registerNum": "Registernummer",
  }

  const renderForm = <T extends string>(
    array: T[],
    sideBySide = false,
    disabled?: {
      [K in T]?: boolean
    }
  ) => {
    const editValue = (e: React.ChangeEvent<HTMLInputElement>) => {
      return setValue({ ...value, [e.target.name]: e.target.value })
    }

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      handleSaveSettings()
    }

    return (
      <form onSubmit={onSubmit} className={classNames({ [styles.sideBySide]: sideBySide })}>
        {array.map((e: string) => (
          <Input
            danger={errors.includes(e)}
            key={e}
            name={e}
            value={value[e]}
            onChange={editValue}
            disabled={disabled && disabled[e as T]}
          >
            {labels[e]}
          </Input>
        ))}
      </form>
    )
  }

  const shopTypes = [
    { label: "- bitte wählen -", value: "", disabled: true },
    { label: "Bäckerei", value: "bakery" },
    { label: "Hofladen", value: "farmersmarket" },
    { label: "Gastronomie", value: "restaurant" },
    { label: "Lebensmittelgeschäft", value: "shop" },
    { label: "Metzgerei", value: "butcher" },
    { label: "Sonstige", value: "others" },
  ]

  const { origin } = useHost()

  return (
    <div className={styles.settings}>
      <HeaderBar name="Allgemeine Einstellungen" saveMethod={handleSaveSettings} saveLoading={saveLoading} />
      <div className={styles.columns}>
        <div className={styles.stack}>
          <div className={styles.card}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <h2 style={{ marginBottom: 0 }}>Zum Betrieb</h2>

              {delayedShop ? (
                <PlatformListingIndicator shopId={delayedShop._ref.id} listed={!!delayedShop.platformListed} />
              ) : null}
            </div>
            {renderForm(["name", "shorthand"])}
            <p>
              {!delayedShop?.offering?.offeringId ? (
                <span style={{ color: "red" }}>Du musst ein Sortiment anlegen bevor du den Link nutzen kannst.</span>
              ) : null}
              <CopyableString stringToCopy={`${origin}/${sanitizeUrl(value.shorthand) || ""}`}>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <ClipboardPlus color={"currentColor"} size="24" title="Anklicken zum Kopieren" />{" "}
                  <span style={{ lineHeight: 1 }}>
                    Dein öffentlicher Link zum Shop:
                    <br />
                    <span style={{ color: "currentColor" }}>{`${origin}/${sanitizeUrl(value.shorthand) || ""}`}</span>
                  </span>
                </div>
              </CopyableString>
            </p>
            <Select<string>
              {...{
                label: "Art des Betriebs",
                options: shopTypes,
                value: value.type,
                onChange: e => e !== undefined && setValue({ ...value, type: e }),
                error: errors.includes("type"),
              }}
            />
            <div>
              <span style={{ fontSize: "1rem", margin: "0 1rem" }}>Logo: </span>
              <ImageUploader
                image={typeof delayedShop?.image === "string" ? { url: delayedShop?.image } : delayedShop?.image}
                setImage={image => {
                  delayedShop && updateShop({ image: image || deleteField() })
                }}
                relatedDocument={delayedShop?._ref}
              />
            </div>
            <div>
              <Toggle
                activeColorOverwrite="var(--primary)"
                state={!!delayedShop?.showInApp}
                setState={v =>
                  updateShop({
                    showInApp: v,
                  })
                }
              >
                In der App und auf der Entdecken-Seite anzeigen
              </Toggle>
            </div>
          </div>
        </div>
        <div className={styles.stack}>
          <div className={styles.card}>
            <h2>Adresse und Kontakt</h2>
            <p>
              Diese Daten werden zur Rechnungsstellung (getpacked an dich), <br /> sowie für dein Impressum genutzt
            </p>
            {renderForm(["contact.companyName"])}
            <h3>Vertretungsberechtigter</h3>
            {renderForm(["contact.representativeFirstName", "contact.representativeLastName"], true)}
            <h3>Kontakt</h3>
            {renderForm(["contact.address"], false)}
            {renderForm(["contact.zipCode", "contact.city"], true)}
            {renderForm(["contact.phone", "contact.email", "contact.website"], true)}
          </div>
        </div>
        <div className={styles.stack}>
          <div className={styles.card}>
            <h2>Rechtliches</h2>
            <p>
              Diese Links werden im Impressung deines Shops angezeigt.
              <br /> Bitte gib sie inkl &quot;https://&quot; an. <b>Optionale Angabe</b>
            </p>
            {renderForm(["linkedInfos.terms", "linkedInfos.dataPrivacy"])}
            <h3>Handelsregister</h3>
            {renderForm(["contact.registerNum", "contact.registerTown"], true)}

            {renderForm(["contact.vatNum"], undefined, {
              "contact.vatNum": settings?.b2b?.active,
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

interface PlatfromListingIndicatorProps {
  shopId: string
  listed: boolean
}
function PlatformListingIndicator({ shopId, listed }: PlatfromListingIndicatorProps) {
  const user = useUser()
  const isGetpackedAdmin = !!user?.getpackedAdmin
  const [reconfirmOpen, setReconfirmOpen] = useState(false)

  const openReconfirm = () => {
    if (!isGetpackedAdmin) return
    setReconfirmOpen(true)
  }
  const toggleListing = () => {
    setReconfirmOpen(false)
    if (!isGetpackedAdmin) return
    updateDoc(getShopRef(shopId), { platformListed: !listed })
  }

  if (reconfirmOpen) {
    return (
      <>
        <button
          className={classNames(styles.actionButton, styles.small, {
            [styles.listingButton]: !isGetpackedAdmin,
            [styles.danger]: listed,
            [styles.success]: !listed,
          })}
          disabled={!isGetpackedAdmin}
          onClick={toggleListing}
        >
          {listed ? "Listing entfernen" : "Listing hinzufügen"}
        </button>
        <button
          className={classNames(styles.actionButton, styles.small, {
            [styles.listingButton]: !isGetpackedAdmin,
          })}
          disabled={!isGetpackedAdmin}
          onClick={() => setReconfirmOpen(false)}
        >
          abbrechen
        </button>
      </>
    )
  }

  if (!listed && isGetpackedAdmin) {
    return (
      <button className={classNames(styles.actionButton, styles.noBorder, styles.small)} onClick={openReconfirm}>
        <span style={{ color: "var(--danger)" }}>
          <CloseCircle color="currentColor" size="12" /> nicht gelistet
        </span>
      </button>
    )
  }

  if (listed) {
    return (
      <button
        className={classNames(styles.actionButton, styles.noBorder, styles.small, {
          [styles.listingButton]: !isGetpackedAdmin,
        })}
        disabled={!isGetpackedAdmin}
        onClick={openReconfirm}
      >
        <span style={{ color: "var(--primary)" }}>
          <Verified color="currentColor" size="12" /> gelistet
        </span>
      </button>
    )
  }

  return <></>
}
